import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import VideoCard from '../components/VideoCard';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Search as SearchIcon, X, Loader2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Search = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setQuery(q);
      handleSearch(q);
    }
  }, []);

  const handleSearch = async (searchQuery = query) => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    setSearched(true);
    setBlocked(false);
    setSearchParams({ q: searchQuery });

    try {
      const response = await axios.get(`${API}/content/videos`, {
        params: { search: searchQuery }
      });
      
      // Check if search was blocked
      if (response.data.blocked) {
        setBlocked(true);
        setVideos([]);
      } else {
        setVideos(response.data.videos || []);
      }
    } catch (error) {
      console.error('Search error:', error);
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSearch();
  };

  const clearSearch = () => {
    setQuery('');
    setVideos([]);
    setSearched(false);
    setSearchParams({});
  };

  return (
    <div className="min-h-screen pt-20 px-4 md:px-8 lg:px-16 py-8" data-testid="search-page">
      <div className="max-w-7xl mx-auto">
        {/* Search Header */}
        <div className="mb-12">
          <h1 
            className="text-4xl md:text-5xl font-normal tracking-wide uppercase mb-6 text-center"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            Search
          </h1>
          
          <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
            <div className="relative">
              <Input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search for videos..."
                className="w-full h-14 bg-[#1A1A1A] border-white/10 text-white placeholder:text-white/30 pl-12 pr-24 text-lg rounded-sm focus:border-white/30"
                data-testid="search-input-main"
              />
              <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
                {query && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="p-2 text-gray-500 hover:text-white transition-colors"
                    data-testid="clear-search-btn"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
                <Button 
                  type="submit"
                  className="bg-white text-black hover:bg-gray-200 h-10"
                  disabled={loading}
                  data-testid="search-submit-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
                </Button>
              </div>
            </div>
          </form>
        </div>

        {/* Results */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={`search-skeleton-${i}`}>
                <div className="aspect-video skeleton mb-3" />
                <div className="h-4 skeleton w-3/4 mb-2" />
                <div className="h-3 skeleton w-1/2" />
              </div>
            ))}
          </div>
        ) : blocked ? (
          <div className="text-center py-20" data-testid="blocked-search">
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-red-400 text-lg mb-2">This search term is not allowed</p>
            <p className="text-gray-600 text-sm">Please search for something else</p>
          </div>
        ) : searched && videos.length === 0 ? (
          <div className="text-center py-20" data-testid="no-results">
            <p className="text-gray-400 text-lg mb-4">No results found for "{searchParams.get('q')}"</p>
            <p className="text-gray-600 text-sm">Try different keywords or browse our categories</p>
          </div>
        ) : videos.length > 0 ? (
          <>
            <div className="flex items-center justify-between mb-6">
              <p className="text-gray-400">
                Found <span className="text-white">{videos.length}</span> results for "{searchParams.get('q')}"
              </p>
            </div>
            <div className="video-grid" data-testid="search-results">
              {videos.map((video, index) => (
                <VideoCard key={video.id || index} video={video} />
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-20" data-testid="search-prompt">
            <p className="text-gray-400">Enter a search term to find videos</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Search;
